import { create } from 'zustand';

/**
 * App-level notice for a session that ended without the user asking (SHCT-622).
 *
 * `SUPERSEDED` — the same account signed in on another device and this session was evicted
 * (PRD 00 §8.3 / PRD 21 §5.1). The spec requires an acknowledged 얼럿, not a passing toast, and our
 * Error Standard §7.1 classes a duplicate-session conflict as a Business Logic Error → Modal 필수.
 *
 * SHCT-861 — §8.3 words the handling as `얼럿 후 초기화`, in that order: the screen resets only once
 * the user presses [확인]. So the eviction's teardown (clear the refresh cookie, sign out of Firebase,
 * drop local auth — which is what makes the route guards jump to /login) is handed to this store as
 * `onAcknowledge` rather than run at 401 time. Until then the operator keeps the screen they were on
 * and can actually read why they are being signed out. The session is already dead server-side, so
 * nothing is granted by the delay; every request underneath keeps failing, which is why `show`
 * refuses to stack a second notice.
 *
 * It lives outside React because the trigger is the axios 401 interceptor, which has no component to
 * render from. {@link SessionNoticeHost} is mounted at the app root, so the modal survives the
 * logout-driven redirect exactly like the toast container does.
 */
export type SessionNoticeKind = 'SUPERSEDED';

interface SessionNoticeState {
  kind: SessionNoticeKind | null;
  onAcknowledge: (() => void) | null;
  show: (kind: SessionNoticeKind, onAcknowledge?: () => void) => void;
  /** Dismiss and run the deferred teardown exactly once. */
  acknowledge: () => void;
}

export const useSessionNoticeStore = create<SessionNoticeState>((set, get) => ({
  kind: null,
  onAcknowledge: null,
  // Never stack a second notice over one the user has not acknowledged: a superseded session fails
  // every in-flight request, so one eviction can produce several 401s in a row. Keeping the FIRST
  // notice also keeps the FIRST teardown, so the deferred logout still runs on 확인.
  show: (kind, onAcknowledge) =>
    set((s) => (s.kind === null ? { kind, onAcknowledge: onAcknowledge ?? null } : s)),
  acknowledge: () => {
    const { onAcknowledge } = get();
    set({ kind: null, onAcknowledge: null });
    onAcknowledge?.();
  },
}));

/** Imperative entry point for non-React callers (the response interceptor). */
export const sessionNotice = {
  show: (kind: SessionNoticeKind, onAcknowledge?: () => void) =>
    useSessionNoticeStore.getState().show(kind, onAcknowledge),
  /**
   * True while a notice is up and unacknowledged.
   *
   * The interceptor needs this because one eviction produces SEVERAL 401s, and
   * only the first is guaranteed to carry `SESSION_SUPERSEDED`. Any companion
   * 401 that lands while this is true is collateral from the same eviction, not
   * an independent session expiry — it must not tear the session down (that
   * redirect is what [확인] is for) and must not raise a second message.
   */
  isPending: () => useSessionNoticeStore.getState().kind !== null,
};
